import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  input,
  model,
  output
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';

/** One row in the geography picker (ISO + display name from your API / list). */
export interface GeographyPickerOption {
  countryName: string;
  iso: string;
  /** When true, row is not selectable (e.g. geography already used). */
  disabled?: boolean;
}

/**
 * Geography-only dropdown: map trigger, titled panel, filter row (select-all when multi),
 * flag + translated country label. Use `multiple` false for single ISO; bind `selectedIso` only in that mode.
 * For reactive forms use `formControlName` / `[formControl]` (implements ControlValueAccessor).
 * For signal parents use `[(selectedIsos)]` or `[(selectedIso)]`.
 */
@Component({
  selector: 'app-novatiq-geographies-picker',
  imports: [FormsModule, TranslateModule, MultiSelectModule, SelectModule, TooltipModule],
  templateUrl: './novatiq-geographies-picker.component.html',
  styleUrl: './novatiq-geographies-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovatiqGeographiesPickerComponent),
      multi: true
    }
  ]
})
export class NovatiqGeographiesPickerComponent implements ControlValueAccessor {
  private readonly translate = inject(TranslateService);

  /** Country rows to show. */
  options = input.required<GeographyPickerOption[]>();

  /** When true, multi-select with select-all; when false, single ISO. */
  multiple = input(true, { transform: booleanAttribute });

  /** Selected ISO codes when `multiple` is true. */
  selectedIsos = model<string[]>([]);

  /**
   * Selected ISO when `multiple` is false.
   * Leave unset when using only multi mode.
   */
  selectedIso = model<string | null>(null);

  inputId = input('novatiqGeographiesPicker');
  /** PrimeNG overlay target, e.g. `'body'` or a host element. */
  appendTo = input<unknown>('body');
  showClear = input(true, { transform: booleanAttribute });

  /** Select-all in panel header; only applies when `multiple` is true. */
  showToggleAll = input(true, { transform: booleanAttribute });

  /** Multi-select chip vs comma summary (multi only). */
  displayFormat = input<'comma' | 'chip'>('comma');

  /**
   * When true, options may use `disabled`; PrimeNG reads the `disabled` property.
   */
  useDisabledOptions = input(false, { transform: booleanAttribute });

  placeholderKey = input('clientMgmtFilterSelectGeography');
  ariaLabelKey = input('clientMgmtFilterSelectGeography');
  panelTitleKey = input('geographiesPickerAvailableCountries');

  readonly valueChange = output<void>();

  readonly panelClass = 'novatiq-geographies-picker-panel';

  private cvaOnChange: (value: string[] | string | null) => void = () => {};
  private cvaOnTouched: () => void = () => {};
  private cvaDisabled = false;

  writeValue(value: string[] | string | null | undefined): void {
    if (this.multiple()) {
      this.selectedIsos.set(Array.isArray(value) ? [...value] : []);
    } else {
      this.selectedIso.set(value == null || value === '' ? null : String(value));
    }
  }

  registerOnChange(fn: (value: string[] | string | null) => void): void {
    this.cvaOnChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.cvaOnTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
  }

  get isControlDisabled(): boolean {
    return this.cvaDisabled;
  }

  onValueChange(): void {
    this.valueChange.emit();
    if (this.multiple()) {
      this.cvaOnChange(this.selectedIsos());
    } else {
      this.cvaOnChange(this.selectedIso());
    }
  }

  onBlur(): void {
    this.cvaOnTouched();
  }

  countryLabel(option: GeographyPickerOption): string {
    const key = `countries.${option.countryName}`;
    const translated = this.translate.instant(key);
    return translated === key ? option.countryName : translated;
  }

  flagClass(iso: string): string {
    return `fi fi-${iso.toLowerCase()}`;
  }
}
